import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Importa motion e AnimatePresence
import { X } from 'lucide-react'; // Importa l'icona X
import styles from './../styles/ClosableTabs.module.css';

export interface ClosableTabItem { // Rinominata e modificata
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  onClose?: () => void; // Nuova proprietà opzionale
  id: string; // Nuova proprietà per identificatore univoco
}

interface ClosableTabsProps { // Rinominata
  tabs: ClosableTabItem[]; // Aggiornato tipo
  defaultActiveTabIndex?: number;
  variant?: 'primary' | 'secondary';
  onTabChange?: (index: number) => void;
}

const ClosableTabs: React.FC<ClosableTabsProps> = ({ tabs, defaultActiveTabIndex = 0, variant = 'primary', onTabChange }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(defaultActiveTabIndex);

  const handleTabClick = (index: number) => {
    if (!tabs[index].disabled) {
      setActiveTabIndex(index);
      if (onTabChange) {
        onTabChange(index);
      }
    }
  };

  const variantClass = variant === 'secondary' ? styles.secondary : styles.primary;

  return (
    <div className={`${styles.tabsContainer} ${variantClass}`}>
      <div className={styles.tabList} role="tablist">
        {tabs.map((tab, index) => (
          <div
            key={tab.id} // Modificata key per usare tab.id
            role="tab"
            aria-selected={activeTabIndex === index}
            aria-controls={`tabpanel-${tab.id}`} // Aggiornato per usare tab.id per unicità
            id={`tab-${tab.id}`} // Aggiornato per usare tab.id
            className={`${styles.tabButton} ${
              activeTabIndex === index ? styles.active : ''
            } ${tab.disabled ? styles.disabled : ''}`}
            onClick={() => handleTabClick(index)}
            onKeyDown={(e) => { // Aggiunto per accessibilità da tastiera
              if (e.key === 'Enter' || e.key === ' ') {
                handleTabClick(index);
              }
            }}
            tabIndex={tab.disabled ? -1 : 0} // Aggiunto per focus e accessibilità
            aria-disabled={tab.disabled} // Aggiunto per accessibilità
          >
            {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
            <span className={styles.tabLabel}>{tab.label}</span>
            {tab.onClose && (
              <button
                className={styles.closeTabButton}
                onClick={(e) => {
                  e.stopPropagation(); // Impedisce al click di propagarsi al div genitore
                  if (tab.onClose) {
                    tab.onClose();
                  }
                }}
                aria-label={`Close ${tab.label}`}
                disabled={tab.disabled} // Disabilita anche il pulsante di chiusura se la tab è disabilitata
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      <AnimatePresence initial={false} mode="wait">
        {tabs.map((tab, index) => {
          if (index !== activeTabIndex) return null;

          return (
            <motion.div
              key={tab.id + '-panel'} // Chiave univoca per AnimatePresence, usando tab.id
              role="tabpanel"
              id={`tabpanel-${tab.id}`} // ID per accessibilità, usando tab.id
              aria-labelledby={`tab-${tab.id}`} // Collegamento alla tab per accessibilità
              className={styles.tabPanel}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              {tab.content}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default ClosableTabs;